import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        home: "Home",
        browse: "Browse Pros",
        postJob: "Post a Job",
        joinAsPro: "Join as a Pro",
        login: "Pro Login",
        faq: "FAQ"
      },
      
      // Hero Section
      hero: {
        title: "Find trusted local pros for any job",
        subtitle: "Connect with verified professionals in your area. From plumbing to remodeling, we've got you covered.",
        searchPlaceholder: "What service do you need?",
        getStarted: "Get Started - It's Free",
        joinAsPro: "Join as a Pro"
      },
      
      // Categories
      categories: {
        title: "Popular Services",
        subtitle: "Browse by category to find the right professional",
        plumbing: "Plumbing",
        electrical: "Electrical",
        hvac: "HVAC",
        handyman: "Handyman",
        cleaning: "Cleaning",
        mechanics: "Mechanics",
        roofing: "Roofing",
        pestControl: "Pest Control",
        remodeling: "Remodeling",
        landscaping: "Landscaping",
        moving: "Moving",
        garageDoor: "Garage Door",
        applianceRepair: "Appliance Repair"
      },
      
      // How it Works
      howItWorks: {
        title: "How it works",
        subtitle: "Simple, fast, and reliable - for homeowners and professionals alike",
        forHomeowners: "For Homeowners",
        forContractors: "For Contractors",
        homeowner: {
          step1Title: "Describe Your Project",
          step1Desc: "Tell us what you need - from a leaky faucet to a full renovation. The more details, the better matches.",
          step2Title: "Get Matched Instantly",
          step2Desc: "Our AI system analyzes your needs and connects you with qualified local pros within minutes.",
          step3Title: "Compare & Connect",
          step3Desc: "Review profiles, read reviews, and chat directly with contractors to find your perfect match.",
          step4Title: "Hire with Confidence",
          step4Desc: "Choose your pro and get the job done. Leave a review to help others in your community."
        },
        contractor: {
          step1Title: "Create Your Profile",
          step1Desc: "Sign up for free. Add your services, service areas, photos of your work, and certifications.",
          step2Title: "Browse Leads",
          step2Desc: "Get notified of new jobs in your area. See job details, budgets, and customer info.",
          step3Title: "Unlock Quality Leads",
          step3Desc: "Pay-per-lead or subscribe monthly. Only pay for leads you want - no upfront fees.",
          step4Title: "Grow Your Business",
          step4Desc: "Build your reputation with reviews. Get verified badge for higher visibility and trust."
        },
        getStartedFree: "Get Started - It's Free",
        joinAsProFree: "Join as a Pro - Free"
      },
      
      // Trust Section
      trust: {
        title: "Why homeowners trust us",
        verifiedPros: "Verified professionals",
        verifiedDesc: "All pros are background-checked and verified before joining.",
        fastResponse: "Fast response times",
        fastDesc: "Get matched within minutes, not days.",
        qualityGuarantee: "Quality guaranteed",
        qualityDesc: "If you're not satisfied, we'll make it right."
      },
      
      // Testimonials
      testimonials: {
        title: "What our customers say",
        subtitle: "Real reviews from homeowners who found their perfect pro on LocalPros"
      },
      
      // Success Stories
      successStories: {
        title: "Pros growing their business with LocalPros",
        subtitle: "See how contractors are transforming their businesses and connecting with more customers",
        becomeSuccess: "Become Our Next Success Story"
      },
      
      // Stats
      stats: {
        totalPros: "Verified Pros",
        jobsCompleted: "Jobs Completed",
        avgRating: "Average Rating",
        serviceCategories: "Service Categories"
      },
      
      // Footer
      footer: {
        tagline: "Connecting homeowners with trusted local professionals.",
        forCustomers: "For Customers",
        findPros: "Find Professionals",
        requestService: "Request Service",
        howItWorks: "How It Works",
        faqs: "FAQs",
        forPros: "For Professionals",
        joinAsPro: "Join as a Pro",
        proResources: "Pro Resources",
        successStories: "Success Stories",
        proSupport: "Pro Support",
        company: "Company",
        aboutUs: "About Us",
        contact: "Contact",
        careers: "Careers",
        press: "Press",
        legal: "Legal",
        terms: "Terms of Service",
        privacy: "Privacy Policy",
        cookies: "Cookie Policy",
        copyright: "© {{year}} LocalPros. All rights reserved."
      },
      
      // Common
      common: {
        loading: "Loading...",
        error: "Error",
        success: "Success",
        submit: "Submit",
        cancel: "Cancel",
        save: "Save",
        delete: "Delete",
        edit: "Edit",
        view: "View",
        search: "Search",
        filter: "Filter",
        all: "All",
        viewAll: "View All",
        learnMore: "Learn More",
        getQuote: "Get a Quote",
        bookNow: "Book Now",
        callNow: "Call Now",
        sendMessage: "Send Message",
        perHour: "/hr",
        perJob: "/job",
        verified: "Verified",
        featured: "Featured",
        pro: "Pro",
        reviews: "reviews",
        rating: "rating",
        miles: "miles",
        back: "Back",
        continue: "Continue",
        submitting: "Submitting...",
        submitRequest: "Submit Request",
        backToHome: "Back to Home",
        creating: "Creating..."
      },
      
      // Forms
      forms: {
        name: "Name",
        email: "Email",
        phone: "Phone",
        password: "Password",
        confirmPassword: "Confirm Password",
        message: "Message",
        description: "Description",
        location: "Location",
        budget: "Budget",
        date: "Date",
        service: "Service",
        required: "Required",
        selectService: "Select a service",
        descriptionPlaceholder: "E.g., I need help fixing a leaky faucet in my kitchen...",
        locationPlaceholder: "City, State or ZIP code",
        preferredDate: "Preferred date",
        budgetPlaceholder: "e.g., $100-200",
        servicesOffered: "Services you offer",
        experience: "Years of experience",
        hourlyRate: "Hourly rate ($)",
        aboutYou: "About you / your services",
        aboutPlaceholder: "Tell customers about your experience, specialties, and why they should hire you...",
        referralCode: "Referral Code (optional)",
        referralPlaceholder: "Enter referral code if you have one",
        referralHint: "Have a friend on LocalPros? Enter their referral code and you both benefit!",
        termsAgreement: "By submitting, you agree to our Terms of Service and Privacy Policy."
      },
      
      // Request Service Page
      requestService: {
        title: "Request a Service",
        subtitle: "Tell us about your project and we'll connect you with the best local pros",
        step1: "Service Details",
        step2: "Contact Information",
        step1Title: "What do you need help with?",
        step1Subtitle: "Tell us about your project so we can match you with the right professionals.",
        step2Title: "How can we reach you?",
        step2Subtitle: "We'll share your details with matched professionals.",
        serviceCategory: "Service Category",
        selectService: "Select a service",
        describeProject: "Describe your project",
        projectPlaceholder: "Tell us more about what you need help with...",
        preferredDate: "Preferred Date",
        estimatedBudget: "Estimated Budget",
        yourName: "Your Name",
        yourEmail: "Your Email",
        yourPhone: "Your Phone",
        yourLocation: "Your Location",
        locationPlaceholder: "City, State or ZIP code",
        back: "Back",
        next: "Next",
        submitRequest: "Submit Request",
        successTitle: "Request Submitted!",
        successMessage: "We've received your service request and are working on matching you with the best professionals in your area.",
        matchedPro: "Matched Pro",
        willContact: "will receive your request and contact you soon. You can also reach out directly:"
      },
      
      // Join as Pro Page
      joinAsPro: {
        title: "Join Our Network",
        headline: "Grow your business with quality leads",
        subtitle: "Connect with homeowners who need your services. Get matched with jobs in your area and build your reputation.",
        benefit1: "Quality leads in your area",
        benefit2: "Flexible pay-per-lead or subscription",
        benefit3: "Build your reputation with reviews",
        benefit4: "Get a verified badge",
        benefit1Title: "Free to Join",
        benefit1Desc: "No signup fees or monthly subscriptions.",
        benefit2Title: "Quality Leads",
        benefit2Desc: "Get matched with serious customers in your area.",
        benefit3Title: "Build Reputation",
        benefit3Desc: "Collect reviews and grow your business.",
        benefit4Title: "Flexible Schedule",
        benefit4Desc: "Accept jobs that fit your availability.",
        whyJoin: "Why join us?",
        createProfile: "Create your profile",
        formSubtitle: "Fill in your details to start receiving leads.",
        services: "services",
        successTitle: "Welcome to the Team!",
        successMessage: "Your profile is now live. Customers can find and contact you for",
        businessName: "Business Name",
        selectServices: "Select services you offer",
        experienceYears: "Years of Experience",
        hourlyRate: "Hourly Rate ($)",
        serviceAreas: "Service Areas",
        serviceAreasPlaceholder: "e.g., Phoenix, Scottsdale, Mesa",
        referralCode: "Referral Code (optional)",
        createAccount: "Create Account",
        alreadyHaveAccount: "Already have an account?",
        loginHere: "Login here"
      },
      
      // Leads Page
      leads: {
        title: "Available Leads",
        subtitle: "Browse and unlock leads in your service area",
        noLeads: "No leads available at the moment",
        unlock: "Unlock Lead",
        unlocked: "Unlocked",
        contactInfo: "Contact Information",
        budgetRange: "Budget Range",
        postedDate: "Posted",
        pricing: "Pricing Plans",
        perLead: "Per Lead",
        basicMonthly: "Basic Monthly",
        proMonthly: "Pro Monthly",
        featuredListing: "Featured Listing",
        subscribe: "Subscribe",
        currentPlan: "Current Plan"
      },
      
      // Profile Page
      profile: {
        aboutMe: "About Me",
        services: "Services",
        portfolio: "Portfolio",
        reviews: "Reviews",
        contactInfo: "Contact Information",
        serviceAreas: "Service Areas",
        writeReview: "Write a Review",
        noReviews: "No reviews yet",
        beFirstReview: "Be the first to review!"
      },
      
      // Dashboard
      dashboard: {
        title: "Dashboard",
        overview: "Overview",
        leads: "Leads",
        messages: "Messages",
        photos: "Photos",
        availability: "Availability",
        quotes: "Quotes",
        reviews: "Reviews",
        verification: "Verification",
        profile: "Profile",
        settings: "Settings",
        logout: "Logout",
        totalLeads: "Total Leads",
        unlockedLeads: "Unlocked Leads",
        pendingContacts: "Pending Contacts",
        avgResponseTime: "Avg Response Time"
      },
      
      // Admin Dashboard
      admin: {
        title: "Admin Dashboard",
        overview: "Dashboard",
        users: "Users",
        contractors: "Contractors",
        jobs: "Job Leads",
        bookings: "Bookings",
        pricing: "Pricing",
        analytics: "Analytics",
        settings: "Settings",
        totalRevenue: "Total Revenue",
        mrr: "Monthly Recurring Revenue",
        totalContractors: "Total Contractors",
        totalJobs: "Total Jobs",
        featureToggles: "Feature Toggles",
        platformFee: "Platform Fee"
      },
      
      // FAQ Page
      faq: {
        title: "Frequently Asked Questions",
        subtitle: "Find answers to common questions about LocalPros",
        searchPlaceholder: "Search for answers...",
        allQuestions: "All Questions",
        homeowners: "For Homeowners",
        contractors: "For Contractors",
        payments: "Payments & Pricing",
        trustSafety: "Trust & Safety",
        stillHaveQuestions: "Still have questions?",
        contactSupport: "Contact Support"
      },
      
      // Booking
      booking: {
        title: "Book Service",
        agreedPrice: "Agreed Service Price",
        scheduledDate: "Preferred Date",
        notes: "Job Description / Notes",
        platformFee: "Platform Fee",
        contractorReceives: "Contractor Receives",
        confirmBooking: "Confirm Booking",
        bookingSuccess: "Booking request sent!"
      },
      
      // Quotes
      quotes: {
        title: "Quote Builder",
        newQuote: "New Quote",
        quoteNumber: "Quote Number",
        customerInfo: "Customer Information",
        lineItems: "Line Items",
        addLine: "Add Line",
        subtotal: "Subtotal",
        tax: "Tax",
        total: "Total",
        validUntil: "Valid Until",
        sendQuote: "Send Quote",
        quoteSent: "Quote sent!",
        draft: "Draft",
        sent: "Sent",
        accepted: "Accepted",
        rejected: "Rejected"
      },
      
      // Availability
      availability: {
        title: "Set Availability",
        selectSlots: "Select Available Time Slots",
        noAvailability: "No availability set"
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    lng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage']
    }
  });

export default i18n;
